export const TRAXION_ANY_PLATFORM = {
  name: 'any',
  nicename: 'Any',
  longname: 'Any Platform',
}

export function parseTraxionLayout(value) {
  const normalizedValue = String(value ?? '')
    .trim()
    .toLowerCase()

  if (normalizedValue === 'mobile' || normalizedValue === 'desktop') {
    return normalizedValue
  }

  return null
}

function normalizeLookupValue(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

function getUniqueNormalizedValues(values) {
  return Array.from(
    new Set(
      values
        .filter((value) => typeof value === 'string' && value.trim().length > 0)
        .map((value) => normalizeLookupValue(value)),
    ),
  )
}

export function findTraxionPlatform(platforms, requestedPlatform) {
  const normalizedRequest = normalizeLookupValue(requestedPlatform)
  if (!normalizedRequest) return null
  if (normalizedRequest === 'any' || normalizedRequest === 'all') {
    return TRAXION_ANY_PLATFORM
  }

  return (
    platforms.find((platform) =>
      getUniqueNormalizedValues([
        platform.name,
        platform.nicename,
        platform.longname,
      ]).includes(normalizedRequest),
    ) || null
  )
}

export function findTraxionGame(games, requestedGame) {
  const normalizedRequest = normalizeLookupValue(requestedGame)
  if (!normalizedRequest) return null

  return (
    games.find((game) =>
      getUniqueNormalizedValues([
        game.name,
        game.nicename,
        game.longname,
        game.abbreviation,
      ]).includes(normalizedRequest),
    ) || null
  )
}

function gameSupportsPlatform(game, platformName) {
  if (platformName === TRAXION_ANY_PLATFORM.name) return true
  if (!game || !platformName) return false

  return Array.isArray(game.platformNames)
    ? game.platformNames.includes(platformName)
    : false
}

function findPreferredPlatformForGame(
  game,
  platforms,
  preferredPlatformNames = [],
) {
  if (
    !game ||
    !Array.isArray(game.platformNames) ||
    game.platformNames.length < 1
  ) {
    return null
  }

  const preferredNames = preferredPlatformNames.filter(Boolean)

  for (const preferredName of preferredNames) {
    if (preferredName === TRAXION_ANY_PLATFORM.name) {
      return TRAXION_ANY_PLATFORM
    }

    if (game.platformNames.includes(preferredName)) {
      return (
        platforms.find((platform) => platform.name === preferredName) || null
      )
    }
  }

  return (
    platforms.find((platform) => game.platformNames.includes(platform.name)) ||
    null
  )
}

function findPreferredGameForPlatform(
  games,
  platformName,
  preferredGameNames = [],
) {
  if (platformName === TRAXION_ANY_PLATFORM.name) {
    for (const preferredName of preferredGameNames.filter(Boolean)) {
      const matchedGame = games.find((game) => game.name === preferredName)
      if (matchedGame) {
        return matchedGame
      }
    }

    return games[0] || null
  }

  const preferredNames = preferredGameNames.filter(Boolean)

  for (const preferredName of preferredNames) {
    const matchedGame = games.find((game) => game.name === preferredName)
    if (matchedGame && gameSupportsPlatform(matchedGame, platformName)) {
      return matchedGame
    }
  }

  return games.find((game) => gameSupportsPlatform(game, platformName)) || null
}

export function getGameDisplayLabel(game) {
  return (
    game?.abbreviation || game?.nicename || game?.longname || game?.name || ''
  )
}

export function getPlatformDisplayLabel(platform) {
  return platform?.nicename || platform?.longname || platform?.name || ''
}

export function buildTraxionEventsSearchHref({ gameName, platformName }) {
  const params = new URLSearchParams({
    page: '1',
  })

  if (gameName) {
    params.set('game', gameName)
  }

  if (platformName && platformName !== TRAXION_ANY_PLATFORM.name) {
    params.set('platforms', platformName)
  }

  return `/sim-racing-events?${params.toString()}`
}

export function buildTraxionEmbedIframeHref({
  gameName,
  platformName,
  layout,
}) {
  const params = new URLSearchParams()

  if (gameName) {
    params.set('game', gameName)
  }

  if (platformName) {
    params.set('platform', platformName)
  }

  const parsedLayout = parseTraxionLayout(layout)
  if (parsedLayout) {
    params.set('layout', parsedLayout)
  }

  const query = params.toString()
  return query
    ? `/embed/traxion/find-races?${query}`
    : '/embed/traxion/find-races'
}

export function resolveTraxionRaceSearchPrefill({
  games = [],
  platforms = [],
  requestedGame,
  requestedPlatform,
  fallbackGame = 'GT7',
  fallbackPlatform = 'PS5',
}) {
  const matchedRequestedGame = findTraxionGame(games, requestedGame)
  const matchedRequestedPlatform = findTraxionPlatform(
    platforms,
    requestedPlatform,
  )
  const fallbackMatchedGame =
    findTraxionGame(games, fallbackGame) || games[0] || null
  const fallbackMatchedPlatform =
    findTraxionPlatform(platforms, fallbackPlatform) || platforms[0] || null

  let selectedGame = matchedRequestedGame || fallbackMatchedGame
  let selectedPlatform = matchedRequestedPlatform || fallbackMatchedPlatform

  if (
    selectedGame &&
    selectedPlatform &&
    gameSupportsPlatform(selectedGame, selectedPlatform.name)
  ) {
    return {
      game: selectedGame,
      platform: selectedPlatform,
    }
  }

  if (matchedRequestedGame) {
    selectedGame = matchedRequestedGame
    selectedPlatform =
      findPreferredPlatformForGame(selectedGame, platforms, [
        matchedRequestedPlatform?.name,
        fallbackMatchedPlatform?.name,
      ]) || selectedPlatform

    return {
      game: selectedGame,
      platform: selectedPlatform,
    }
  }

  if (matchedRequestedPlatform) {
    selectedPlatform = matchedRequestedPlatform
    selectedGame =
      findPreferredGameForPlatform(games, selectedPlatform.name, [
        fallbackMatchedGame?.name,
      ]) || selectedGame

    return {
      game: selectedGame,
      platform: selectedPlatform,
    }
  }

  if (selectedGame) {
    selectedPlatform =
      findPreferredPlatformForGame(selectedGame, platforms, [
        fallbackMatchedPlatform?.name,
      ]) || selectedPlatform
  }

  if (
    selectedPlatform &&
    (!selectedGame ||
      !gameSupportsPlatform(selectedGame, selectedPlatform.name))
  ) {
    selectedGame =
      findPreferredGameForPlatform(games, selectedPlatform.name, [
        fallbackMatchedGame?.name,
      ]) || selectedGame
  }

  return {
    game: selectedGame,
    platform: selectedPlatform,
  }
}
